import cx from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'

import { type ItemProps } from '@frame-builder/lib/item'

type DetailsStatus = 'closed' | 'closing' | 'open' | 'opening'

const openStatuses: DetailsStatus[] = ['opening', 'open']

const Item = ({
  title,
  imageElement,
  subtitleElement,
  descriptionElement,
  detailsElement,
  badgeElement,
  selected = false,
  disabled = false,
  error = false,
  onClick,
  className,
}: ItemProps) => {
  const [detailsStatus, setDetailsStatus] = useState<DetailsStatus>(
    selected ? 'open' : 'closed',
  )

  const detailsContentRef = useRef<HTMLDivElement>(null)

  // Update details status based on selected prop value
  useEffect(() => {
    setDetailsStatus((currentDetailsStatus) => {
      // Check if status does not need to change
      if (selected && currentDetailsStatus === 'open') {
        return currentDetailsStatus
      }

      if (!selected && currentDetailsStatus === 'closed') {
        return currentDetailsStatus
      }

      // Update status
      return selected ? 'opening' : 'closing'
    })
  }, [selected])

  const handleDetailsTransitionEnd = useCallback(() => {
    setDetailsStatus((currentDetailsStatus) => {
      if (currentDetailsStatus === 'opening') {
        return 'open'
      }

      if (currentDetailsStatus === 'closing') {
        return 'closed'
      }

      return currentDetailsStatus
    })
  }, [])

  return (
    <div
      onClick={onClick}
      className={cx(
        'relative border',
        {
          'opacity-30 cursor-not-allowed': disabled,
          'cursor-auto': !disabled && selected,
          'cursor-pointer': !disabled && !selected,
          'bg-white': selected,
          'bg-gray-100': !selected,
          'border-error': error,
          'border-black': !error && selected,
          'border-gray-400': !error && !selected,
        },
        className,
      )}
    >
      <div className="flex p-2 space-x-3 overflow-hidden">
        {imageElement}

        <div className="flex-1 p-1 space-y-1">
          <h4 className="text-sm flex justify-between gap-x-2.5">
            <span>{title}</span>
            <span className="shrink-0">{badgeElement}</span>
          </h4>

          {!!subtitleElement && <p className="text-xs">{subtitleElement}</p>}
          {!!descriptionElement && (
            <div className="flex text-xs">{descriptionElement}</div>
          )}
        </div>
      </div>

      {!!detailsElement && (
        <div
          onTransitionEnd={handleDetailsTransitionEnd}
          className="w-full transition-all duration-200 overflow-hidden"
          style={{
            height: openStatuses.includes(detailsStatus)
              ? (detailsContentRef.current?.clientHeight ?? 'auto')
              : 0,
          }}
        >
          <div ref={detailsContentRef}>{detailsElement}</div>
        </div>
      )}
    </div>
  )
}

export default Item
